import { clsx } from 'clsx'

interface ButtonProps {
  children?: React.ReactNode
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'icon-xs' | 'icon-sm' | 'icon-md'
  disabled?: boolean
  loading?: boolean
  className?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  form?: string
  'aria-label'?: string
}

// ─── Consistent height tiers (matches all form inputs & selects) ────────────
// xs:      h-6  (24px)  — very compact, rare
// sm:      h-8  (32px)  — compact tables, inline actions
// md:      h-10 (40px)  — default forms, buttons beside inputs
// lg:      h-12 (48px)  — hero CTAs, prominent actions
// icon-xs: h-7 w-7      — mobile row actions, tight spaces
// icon-sm: h-8 w-8      — desktop table row actions
// icon-md: h-9 w-9      — standard icon buttons (modal close, etc.)

const ICON_SIZES = new Set(['icon-xs', 'icon-sm', 'icon-md'])

export default function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className = '',
  icon,
  iconPosition = 'left',
  fullWidth = false,
  form,
  'aria-label': ariaLabel,
}: ButtonProps) {
  const isIconSize = ICON_SIZES.has(size)
  // Legacy: icon-only detection for sm/md/lg sizes (icon prop + no children)
  const isIconOnly = isIconSize || (icon && !children)

  const baseClasses = clsx(
    'inline-flex items-center justify-center font-medium rounded-md',
    'transition-all duration-150 ease-out',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-page)]',
    'cursor-pointer select-none',
    'active:scale-[0.97]',
  )

  const variantClasses = {
    primary: clsx(
      'bg-[var(--brand-primary)] text-[var(--text-on-brand)]',
      'hover:brightness-110',
      'btn-depth-primary',
    ),
    secondary: clsx(
      'text-[var(--text-secondary)] bg-[var(--surface-card)]',
      'border border-[var(--border-input)]',
      'hover:bg-[var(--surface-hover)] hover:border-[var(--border-strong)]',
      'btn-depth-secondary',
    ),
    ghost: 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
    danger: clsx(
      'text-[var(--status-error-text)] bg-[var(--status-error-bg)]',
      'border border-[var(--status-error-border)]',
      'hover:bg-[var(--status-error-text)] hover:text-[var(--text-on-brand)] hover:border-transparent',
      'btn-depth-danger',
    ),
    success: clsx(
      'text-[var(--status-success-text)] bg-[var(--status-success-bg)]',
      'border border-[var(--status-success-border)]',
      'hover:bg-[var(--status-success-text)] hover:text-[var(--text-on-brand)] hover:border-transparent',
      'btn-depth-success',
    ),
    outline: clsx(
      'text-[var(--brand-primary)] border border-[var(--brand-primary)]',
      'hover:bg-[var(--brand-primary-subtle)]',
      'btn-depth-outline',
    ),
  }

  const sizeClasses: Record<string, string> = {
    xs:      'h-6 px-2 text-xs gap-1',
    sm:      'h-8 px-3 text-xs gap-1.5',
    md:      'h-10 px-4 text-sm gap-2',
    lg:      'h-12 px-6 text-base gap-2',
    // Icon-only sizes — no padding, square
    'icon-xs': 'h-7 w-7',
    'icon-sm': 'h-8 w-8',
    'icon-md': 'h-9 w-9',
    // Legacy: icon prop without children on sm/md/lg — square
    ...(isIconOnly && !isIconSize ? {
      sm: 'h-8 w-8',
      md: 'h-10 w-10',
      lg: 'h-12 w-12',
    } : {}),
  }

  const disabledClasses = (disabled || loading) ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      form={form}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        disabledClasses,
        fullWidth && 'w-full',
        className
      )}
    >
      {loading ? (
        <>
          <span className={clsx(
            'animate-spin border-2 border-current border-t-transparent rounded-full',
            (size === 'xs' || size === 'sm') ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'
          )} />
          {children && <span>{children}</span>}
        </>
      ) : isIconSize ? (
        icon
      ) : isIconOnly ? (
        icon
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <span className="shrink-0">{icon}</span>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <span className="shrink-0">{icon}</span>
          )}
        </>
      )}
    </button>
  )
}
